import { useQuery, useQueryClient } from '@tanstack/react-query';
import { InsightsData } from '../types/insights';
import { fetchInsights } from '../utils/api';
import { queryKeys } from '../lib/queryClient';

interface UseInsightsDataOptions {
  userId: string;
  date: string;
  enabled?: boolean;
}

export const useInsightsData = ({ userId, date, enabled = true }: UseInsightsDataOptions) => {
  const queryClient = useQueryClient();
  
  const query = useQuery<InsightsData | null>({
    queryKey: queryKeys.insights(userId, date),
    queryFn: () => fetchInsights(userId, date),
    enabled: Boolean(userId && date && enabled),
    // Keep data fresh for 30 seconds to reduce excessive refetching
    staleTime: 30 * 1000, // 30 seconds
    // Keep insights data in cache longer since it's expensive to regenerate
    gcTime: 15 * 60 * 1000, // 15 minutes
    // Don't automatically refetch insights on window focus (only when pipeline completes)
    refetchOnWindowFocus: false,
    // Only refetch on mount if data is stale or missing
    refetchOnMount: true,
    // Don't refetch on reconnect unless data is stale
    refetchOnReconnect: false,
    // Keep previous data while fetching new data
    placeholderData: (previousData: InsightsData | null | undefined) => previousData,
    // Custom error handling for insights
    retry: (failureCount: number, error: any) => {
      // Don't retry 404s (no data found)
      if (error?.response?.status === 404) return false;
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
  });

  // Helper function to manually refresh insights
  const refreshInsights = () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.insights(userId, date),
    });
  };

  // Helper function to prefetch insights for a different date/user
  const prefetchInsights = (prefetchUserId: string, prefetchDate: string) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.insights(prefetchUserId, prefetchDate),
      queryFn: () => fetchInsights(prefetchUserId, prefetchDate),
      staleTime: 30 * 1000,
    });
  };

  return {
    ...query,
    insights: query.data,
    refreshInsights,
    prefetchInsights,
    // Helper flags
    hasInsights: Boolean(query.data?.insights?.length),
    hasMetrics: Boolean(query.data?.metrics),
  };
};

// Hook to invalidate insights when pipelines complete
export const useInsightsInvalidation = () => {
  const queryClient = useQueryClient();
  
  const invalidateInsights = (userId: string, date: string) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.insights(userId, date),
    });
  };

  const invalidateAllInsights = () => {
    return queryClient.invalidateQueries({
      queryKey: ['insights'],
    });
  };

  return {
    invalidateInsights,
    invalidateAllInsights,
  };
};